// People → Management: labeled tables that do not load until clicked
// (owner, 2026-08-19 / 2026-08-21). Owner/director only. Chips stay put;
// Loans and Bonuses open real tables underneath.
// Pay & Hours lives only on the People page's own "Pay & Hours" tab
// (PayHours.tsx) — one table, not twice (owner, 2026-09-09). Annual bonuses
// now come from that same tab (1,200 hours in the anniversary year), so the
// Bonuses chip here keeps only Referrals and Sign-On.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { EmptyState } from './FaultState';
import { peekResource, persistResource, refetchResource, useResource } from '../lib/store';
import { canSeeManagementTab } from './ClinicTabs';
import { session } from '../lib/session';
import { LOCATION_NAMES, orderLocations, type LocationCode } from '../lib/locations';
import { useWrite } from './MoneyForms';
import {
  addPeopleMgmtRow, deletePeopleMgmtRow, loadPeopleMgmt, peopleMgmtKey, updatePeopleMgmtRow,
  type MgmtRow, type PeopleMgmtPayload,
} from '../lib/peopleMgmt';
import { compareByLastName } from '../lib/personName';

export const PEOPLE_MGMT_CUSTOM_KEY = 'people-mgmt-custom';

export const PEOPLE_MGMT_ITEMS = [
  { id: 'bonuses', label: 'Bonuses', blurb: 'Referrals and Sign-On.' },
  { id: 'pto', label: 'PTO', blurb: 'PTO tracker.' },
  { id: 'loans', label: 'Loans', blurb: 'Loans we give employees — from the payroll dashboard Loan tab.' },
  { id: 'insurance', label: 'Insurance', blurb: 'Insurance deductions.' },
] as const;

export interface PeopleMgmtItem {
  id: string;
  label: string;
  blurb: string;
}

interface RosterPerson {
  id: number;
  file_no?: string | null;
  full_name: string;
  role: string;
  home_location_code: string;
  status: string;
}

function loadCustom(): PeopleMgmtItem[] {
  return peekResource<PeopleMgmtItem[]>(PEOPLE_MGMT_CUSTOM_KEY) ?? [];
}

function isBuiltin(id: string): boolean {
  return PEOPLE_MGMT_ITEMS.some((item) => item.id === id);
}

export function PeopleManagement(): JSX.Element {
  const [openId, setOpenId] = useState<string | null>(null);
  const [custom, setCustom] = useState<PeopleMgmtItem[]>(loadCustom);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [confirmStep, setConfirmStep] = useState<'sure' | 'positive' | null>(null);
  const roster = useResource<{ people: RosterPerson[] }>('people-roster', async () => ({ people: [] }));
  const people = (roster.status === 'ready' ? roster.data.people : [])
    .filter((p) => p.status === 'ACTIVE');

  const items: PeopleMgmtItem[] = [...PEOPLE_MGMT_ITEMS, ...custom];
  const open = items.find((item) => item.id === openId) ?? null;
  const canDelete = canSeeManagementTab() && open !== null && !isBuiltin(open.id);
  const liveTable = open && (open.id === 'bonuses' || open.id === 'loans');

  async function addTable(ev: Event): Promise<void> {
    ev.preventDefault();
    const label = newLabel.trim();
    if (!label) return;
    const taken = items.some((item) => item.label.toLowerCase() === label.toLowerCase());
    if (taken) return;
    const next: PeopleMgmtItem[] = [
      ...custom,
      { id: `custom-${Date.now()}`, label, blurb: 'Added as an idea — not populated yet.' },
    ];
    setCustom(next);
    await persistResource(PEOPLE_MGMT_CUSTOM_KEY, next);
    setNewLabel('');
    setAdding(false);
  }

  function toggle(id: string): void {
    setConfirmStep(null);
    setOpenId((current) => (current === id ? null : id));
  }

  async function deleteOpen(): Promise<void> {
    if (!open || isBuiltin(open.id) || !canSeeManagementTab()) return;
    const next = custom.filter((item) => item.id !== open.id);
    setCustom(next);
    await persistResource(PEOPLE_MGMT_CUSTOM_KEY, next);
    setOpenId(null);
    setConfirmStep(null);
  }

  return (
    <div data-testid="people-mgmt-menu">
      <div class="section-head">
        <h2>Management</h2>
        <p>Nothing loads until you open a table.</p>
      </div>
      <div class="mgmt-menu">
        {items.map((item) => (
          <button
            type="button"
            class="btn"
            key={item.id}
            data-testid={`people-mgmt-open-${item.id}`}
            aria-pressed={item.id === openId}
            onClick={() => toggle(item.id)}
          >
            {item.label}
          </button>
        ))}
        {adding ? null : (
          <button
            type="button"
            class="btn"
            data-testid="people-mgmt-add"
            onClick={() => setAdding(true)}
          >
            Add table
          </button>
        )}
      </div>
      {adding ? (
        <form class="mgmt-add" onSubmit={(ev) => { void addTable(ev); }}>
          <input
            type="text"
            aria-label="New table name"
            data-testid="people-mgmt-add-name"
            placeholder="Table name"
            value={newLabel}
            onInput={(ev: Event) => setNewLabel((ev.target as HTMLInputElement).value)}
          />
          <button type="submit" class="btn" data-testid="people-mgmt-add-save" disabled={!newLabel.trim()}>
            Add
          </button>
          <button
            type="button"
            class="btn"
            data-testid="people-mgmt-add-cancel"
            onClick={() => { setAdding(false); setNewLabel(''); }}
          >
            Cancel
          </button>
        </form>
      ) : null}
      {open ? (
        <section class="card mgmt-folder" data-testid={`people-mgmt-table-${open.id}`}>
          <div class="section-head">
            <div class="section-head-row">
              <h2>{open.label}</h2>
              {canDelete && confirmStep === null ? (
                <div class="section-head-actions">
                  <button
                    type="button"
                    class="btn"
                    data-testid="people-mgmt-delete"
                    onClick={() => setConfirmStep('sure')}
                  >
                    Delete
                  </button>
                </div>
              ) : null}
            </div>
            {confirmStep === 'sure' ? (
              <div class="mgmt-add" data-testid="people-mgmt-delete-sure">
                <p>Are you sure you want to delete {open.label}?</p>
                <button type="button" class="btn" data-testid="people-mgmt-delete-sure-yes" onClick={() => setConfirmStep('positive')}>Yes</button>
                <button type="button" class="btn" data-testid="people-mgmt-delete-cancel" onClick={() => setConfirmStep(null)}>Cancel</button>
              </div>
            ) : null}
            {confirmStep === 'positive' ? (
              <div class="mgmt-add" data-testid="people-mgmt-delete-positive">
                <p>Are you positive?</p>
                <button type="button" class="btn" data-testid="people-mgmt-delete-positive-yes" onClick={() => { void deleteOpen(); }}>Yes, OK</button>
                <button type="button" class="btn" data-testid="people-mgmt-delete-cancel" onClick={() => setConfirmStep(null)}>Cancel</button>
              </div>
            ) : null}
            {confirmStep === null && open.blurb ? <p>{open.blurb}</p> : null}
          </div>
          {confirmStep === null && liveTable ? (
            <MgmtLive group={open.id} people={people} />
          ) : null}
          {confirmStep === null && !liveTable ? <EmptyState label="No rows yet — label only." /> : null}
        </section>
      ) : null}
    </div>
  );
}

function MgmtLive({ group, people }: { group: string; people: RosterPerson[] }): JSX.Element {
  const load = useResource<PeopleMgmtPayload>(
    peopleMgmtKey(group),
    () => loadPeopleMgmt(group),
  );
  const { busy, error, run } = useWrite();

  async function reload(): Promise<void> {
    await refetchResource(peopleMgmtKey(group), () => loadPeopleMgmt(group));
  }

  if (load.status !== 'ready' && load.status !== 'fault') {
    return <p>Loading…</p>;
  }
  if (load.status === 'fault') {
    return <p role="alert">{load.error}</p>;
  }
  const data = load.data;

  return (
    <div>
      {error ? <p role="alert">{error}</p> : null}
      {group === 'loans' ? (
        <LoanTable
          rows={data.rows ?? []}
          people={people}
          busy={busy}
          onAdd={(body) => run(() => addPeopleMgmtRow(body), () => { void reload(); })}
          onUpdate={(id, body) => run(() => updatePeopleMgmtRow(id, body), () => { void reload(); })}
          onDelete={(id) => run(() => deletePeopleMgmtRow(id), () => { void reload(); })}
        />
      ) : null}
      {group === 'bonuses' ? (
        <BonusTables
          rows={data.rows ?? []}
          people={people}
          busy={busy}
          onAdd={(body) => run(() => addPeopleMgmtRow(body), () => { void reload(); })}
          onUpdate={(id, body) => run(() => updatePeopleMgmtRow(id, body), () => { void reload(); })}
          onDelete={(id) => run(() => deletePeopleMgmtRow(id), () => { void reload(); })}
        />
      ) : null}
    </div>
  );
}

function ClinicSelect({ value, onInput }: { value: string; onInput: (v: string) => void }): JSX.Element {
  const codes = orderLocations((session.value.me?.locations ?? []) as LocationCode[]);
  return (
    <select aria-label="Clinic" value={value} onChange={(e: Event) => onInput((e.target as HTMLSelectElement).value)}>
      {codes.map((code) => (
        <option key={code} value={code}>{LOCATION_NAMES[code] ?? code}</option>
      ))}
    </select>
  );
}

function PersonSelect({
  people, value, onInput, label, clinic,
}: { people: RosterPerson[]; value: string; onInput: (v: string) => void; label: string; clinic?: string }): JSX.Element {
  const list = (clinic ? people.filter((p) => p.home_location_code === clinic) : people)
    .slice()
    .sort((a, b) => compareByLastName(a.full_name, b.full_name));
  return (
    <select aria-label={label} value={value} onChange={(e: Event) => onInput((e.target as HTMLSelectElement).value)}>
      <option value="">{label}…</option>
      {list.map((p) => (
        <option key={p.id} value={p.id}>{p.full_name}</option>
      ))}
    </select>
  );
}

function LoanTable({
  rows, people, busy, onAdd, onUpdate, onDelete,
}: {
  rows: MgmtRow[]; people: RosterPerson[]; busy: boolean;
  onAdd: (body: Record<string, unknown>) => void;
  onUpdate: (id: number, body: Record<string, unknown>) => void;
  onDelete: (id: number) => void;
}): JSX.Element {
  const defaultClinic: string = orderLocations((session.value.me?.locations ?? []) as LocationCode[])[0] ?? 'MELM';
  // Owner rule 2026-09-10: every table has Add and Edit. `editing` holds the
  // row Edit was clicked on — same add form, prefilled, swaps "Add loan" for
  // "Save" and routes submit to onUpdate instead of onAdd (pattern copied
  // from CompanyLicenses.tsx's startEdit/form.id).
  const [editing, setEditing] = useState<MgmtRow | null>(null);
  const [clinic, setClinic] = useState(defaultClinic);
  const [employeeId, setEmployeeId] = useState('');
  const [dateOn, setDateOn] = useState('');
  const [amount, setAmount] = useState('');
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [p3, setP3] = useState('');
  const [p4, setP4] = useState('');
  const [link, setLink] = useState('');

  function onDone(): void {
    setEditing(null);
    setClinic(defaultClinic);
    setEmployeeId('');
    setDateOn('');
    setAmount('');
    setP1(''); setP2(''); setP3(''); setP4('');
    setLink('');
  }

  function startEdit(row: MgmtRow): void {
    setEditing(row);
    setClinic(row.locationCode);
    setEmployeeId(String(row.employeeId));
    setDateOn(row.dateOn ?? '');
    setAmount(row.amount != null ? String(row.amount) : '');
    setP1(row.payDate1 ?? '');
    setP2(row.payDate2 ?? '');
    setP3(row.payDate3 ?? '');
    setP4(row.payDate4 ?? '');
    setLink(row.linkUrl ?? '');
  }

  return (
    <div>
      <p class="hint">Add loan payments to deductions on the matching payroll week.</p>
      <form class="money-add" onSubmit={(ev) => {
        ev.preventDefault();
        const body = {
          kind: 'loan', locationCode: clinic, employeeId: Number(employeeId),
          dateOn, amount, payDate1: p1, payDate2: p2, payDate3: p3, payDate4: p4, linkUrl: link,
        };
        if (editing) onUpdate(editing.id, body);
        else onAdd(body);
        onDone();
      }}>
        <ClinicSelect value={clinic} onInput={setClinic} />
        <PersonSelect people={people} value={employeeId} onInput={setEmployeeId} label="Employee" clinic={clinic} />
        <input type="date" aria-label="Date loan made" value={dateOn} onInput={(e: Event) => setDateOn((e.target as HTMLInputElement).value)} />
        <input type="number" step="0.01" min="0" placeholder="$ Amount" aria-label="$ Amount" value={amount} onInput={(e: Event) => setAmount((e.target as HTMLInputElement).value)} />
        <input type="text" placeholder="Pay date 1" aria-label="Pay date 1" value={p1} onInput={(e: Event) => setP1((e.target as HTMLInputElement).value)} />
        <input type="text" placeholder="Pay date 2" aria-label="Pay date 2" value={p2} onInput={(e: Event) => setP2((e.target as HTMLInputElement).value)} />
        <input type="text" placeholder="Pay date 3" aria-label="Pay date 3" value={p3} onInput={(e: Event) => setP3((e.target as HTMLInputElement).value)} />
        <input type="text" placeholder="Pay date 4" aria-label="Pay date 4" value={p4} onInput={(e: Event) => setP4((e.target as HTMLInputElement).value)} />
        <input type="text" placeholder="Link to agreement" aria-label="Link" value={link} onInput={(e: Event) => setLink((e.target as HTMLInputElement).value)} />
        <div class="service-point-actions">
          <button type="submit" class="btn primary" disabled={busy || !employeeId}>{editing ? 'Save' : 'Add loan'}</button>
          {editing ? <button type="button" class="btn" disabled={busy} onClick={onDone}>Cancel</button> : null}
        </div>
      </form>
      <table class="data" data-testid="people-mgmt-loans-table">
        <thead>
          <tr>
            <th class="name">Employee</th><th>Date loan made</th><th>$ Amount</th>
            <th>Pay date 1</th><th>Pay date 2</th><th>Pay date 3</th><th>Pay date 4</th><th>Link</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={9}>No loans yet.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id}>
              <td class="name">{r.employeeName}</td>
              <td>{r.dateOn}</td>
              <td class="num">{r.amount}</td>
              <td>{r.payDate1}</td><td>{r.payDate2}</td><td>{r.payDate3}</td><td>{r.payDate4}</td>
              <td>{r.linkUrl ? <a href={r.linkUrl} target="_blank" rel="noreferrer">Open</a> : ''}</td>
              <td>
                <button type="button" class="btn" data-testid={`people-mgmt-edit-${r.id}`} disabled={busy} onClick={() => startEdit(r)}>Edit</button>
                <button type="button" class="btn" disabled={busy} onClick={() => onDelete(r.id)}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BonusTables({
  rows, people, busy, onAdd, onUpdate, onDelete,
}: {
  rows: MgmtRow[]; people: RosterPerson[]; busy: boolean;
  onAdd: (body: Record<string, unknown>) => void;
  onUpdate: (id: number, body: Record<string, unknown>) => void;
  onDelete: (id: number) => void;
}): JSX.Element {
  return (
    <div>
      <BonusBlock
        title="Referrals"
        kind="referral"
        rows={rows.filter((r) => r.kind === 'referral')}
        people={people}
        busy={busy}
        onAdd={onAdd}
        onUpdate={onUpdate}
        onDelete={onDelete}
        referred
      />
      <BonusBlock
        title="Sign-On"
        kind="signon"
        rows={rows.filter((r) => r.kind === 'signon')}
        people={people}
        busy={busy}
        onAdd={onAdd}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
    </div>
  );
}

function BonusBlock({
  title, kind, rows, people, busy, onAdd, onUpdate, onDelete, referred,
}: {
  title: string; kind: string; rows: MgmtRow[]; people: RosterPerson[]; busy: boolean;
  onAdd: (body: Record<string, unknown>) => void;
  onUpdate: (id: number, body: Record<string, unknown>) => void;
  onDelete: (id: number) => void;
  referred?: boolean;
}): JSX.Element {
  const defaultClinic: string = orderLocations((session.value.me?.locations ?? []) as LocationCode[])[0] ?? 'MELM';
  // Owner rule 2026-09-10: every table has Add and Edit — same pattern as
  // LoanTable's `editing` above.
  const [editing, setEditing] = useState<MgmtRow | null>(null);
  const [clinic, setClinic] = useState(defaultClinic);
  const [employeeId, setEmployeeId] = useState('');
  const [related, setRelated] = useState('');
  const [dateOn, setDateOn] = useState('');
  const [amount, setAmount] = useState('');
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [notes, setNotes] = useState('');

  function onDone(): void {
    setEditing(null);
    setClinic(defaultClinic);
    setEmployeeId('');
    setRelated('');
    setDateOn('');
    setAmount('');
    setP1(''); setP2('');
    setNotes('');
  }

  function startEdit(row: MgmtRow): void {
    setEditing(row);
    setClinic(row.locationCode);
    setEmployeeId(String(row.employeeId));
    setRelated(row.relatedEmployeeId != null ? String(row.relatedEmployeeId) : '');
    setDateOn(row.dateOn ?? '');
    setAmount(row.amount != null ? String(row.amount) : '');
    setP1(row.payDate1 ?? '');
    setP2(row.payDate2 ?? '');
    setNotes(row.notes ?? '');
  }

  return (
    <div class="card" style={{ marginTop: '1rem' }}>
      <div class="section-head"><h2>{title}</h2></div>
      <form class="money-add" onSubmit={(ev) => {
        ev.preventDefault();
        const body = {
          kind, locationCode: clinic, employeeId: Number(employeeId),
          relatedEmployeeId: referred && related ? Number(related) : null,
          dateOn, amount, payDate1: p1, payDate2: p2, notes,
        };
        if (editing) onUpdate(editing.id, body);
        else onAdd(body);
        onDone();
      }}>
        <ClinicSelect value={clinic} onInput={setClinic} />
        <PersonSelect people={people} value={employeeId} onInput={setEmployeeId} label={referred ? 'Current employee' : 'Employee'} clinic={clinic} />
        {referred ? <PersonSelect people={people} value={related} onInput={setRelated} label="Referred employee" /> : null}
        <input type="date" aria-label="Hire date" value={dateOn} onInput={(e: Event) => setDateOn((e.target as HTMLInputElement).value)} />
        <input type="number" step="0.01" min="0" placeholder="$ Amount" aria-label="$ Amount" value={amount} onInput={(e: Event) => setAmount((e.target as HTMLInputElement).value)} />
        <input type="text" placeholder="Pay date 1" aria-label="Pay date 1" value={p1} onInput={(e: Event) => setP1((e.target as HTMLInputElement).value)} />
        <input type="text" placeholder="Pay date 2" aria-label="Pay date 2" value={p2} onInput={(e: Event) => setP2((e.target as HTMLInputElement).value)} />
        <input type="text" placeholder="Notes" aria-label="Notes" value={notes} onInput={(e: Event) => setNotes((e.target as HTMLInputElement).value)} />
        <div class="service-point-actions">
          <button type="submit" class="btn primary" disabled={busy || !employeeId}>{editing ? 'Save' : 'Add'}</button>
          {editing ? <button type="button" class="btn" disabled={busy} onClick={onDone}>Cancel</button> : null}
        </div>
      </form>
      <table class="data">
        <thead>
          <tr>
            {referred ? <><th class="name">Current employee</th><th>Referred employee</th></> : <th class="name">Employee</th>}
            <th>Hire date</th>
            <th>$ Amount</th><th>Pay date 1</th><th>Pay date 2</th><th>Notes</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={8}>None yet.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id}>
              <td class="name">{r.employeeName}</td>
              {referred ? <td>{r.relatedEmployeeName}</td> : null}
              <td>{r.dateOn}</td>
              <td class="num">{r.amount}</td>
              <td>{r.payDate1}</td><td>{r.payDate2}</td><td>{r.notes}</td>
              <td>
                <button type="button" class="btn" data-testid={`people-mgmt-edit-${r.id}`} disabled={busy} onClick={() => startEdit(r)}>Edit</button>
                <button type="button" class="btn" disabled={busy} onClick={() => onDelete(r.id)}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
